import React, { useCallback, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Switch,
  Tooltip,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarehouseRankings from "./WarehouseRankings";
import { debounce } from "../../../utilities/debounce";

// Debounce function
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

// Custom equality function for memo
const arePropsEqual = (prevProps, nextProps) => {
  // Only re-render if the specific vendor's preferences changed
  const prevVendorPrefs = prevProps.preferences[prevProps.row.id] || {};
  const nextVendorPrefs = nextProps.preferences[nextProps.row.id] || {};
  
  // Deep comparison of warehouses arrays
  const prevWarehouses = prevVendorPrefs.warehouses || [];
  const nextWarehouses = nextVendorPrefs.warehouses || [];
  
  // If array lengths differ, they're not equal
  if (prevWarehouses.length !== nextWarehouses.length) {
    return false;
  }
  
  // Compare each warehouse by id and name
  for (let i = 0; i < prevWarehouses.length; i++) {
    if (prevWarehouses[i].id !== nextWarehouses[i].id || 
        prevWarehouses[i].name !== nextWarehouses[i].name) {
      return false;
    }
  }
  
  return (
    prevProps.row.id === nextProps.row.id &&
    prevVendorPrefs.disabled === nextVendorPrefs.disabled &&
    prevVendorPrefs.free_shipping_minimum === nextVendorPrefs.free_shipping_minimum
  );
};

// Wrap in memo to prevent unnecessary re-renders with custom equality function
const VendorDropdown = React.memo(({ row, preferences = {}, onPreferencesUpdate }) => {
  // Get only the specific vendor preferences we need
  const vendorPrefs = preferences[row.id] || {};
  
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isDisabled, setIsDisabled] = React.useState(
    vendorPrefs.disabled ?? false
  );
  // const [isDropshipEnabled, setIsDropshipEnabled] = React.useState(preferences[row.id]?.is_dropshipping_enabled ?? false); // Commented out dropshipping
  const [freeShipMin, setFreeShipMin] = React.useState(
    (
      vendorPrefs.free_shipping_minimum ?? row.freeShippingMinimum
    ).toString()
  );

  // Update effect to use disabled flag - with better dependencies
  React.useEffect(() => {
    const currentVendorPrefs = preferences[row.id] || {};
    setIsDisabled(currentVendorPrefs.disabled ?? false);
    // setIsDropshipEnabled(preferences[row.id]?.is_dropshipping_enabled ?? false); // Commented out dropshipping
    setFreeShipMin(
      (
        currentVendorPrefs.free_shipping_minimum ?? row.freeShippingMinimum
      ).toString()
    );
  }, [preferences[row.id], row.id, row.freeShippingMinimum]);

  // Create debounced handler for the switch
  const debouncedSwitchUpdate = useCallback(
    debounce((newValue) => {
      onPreferencesUpdate(row.id, { disabled: newValue });
    }, 300),
    [row.id, onPreferencesUpdate]
  );

  const handleSwitchChange = (event) => {
    event.stopPropagation();
    const newValue = !event.target.checked;
    setIsDisabled(newValue);
    debouncedSwitchUpdate(newValue);
  };

  // Commented out dropshipping handler
  /*const handleDropshipCheckboxClick = (event) => {
    event.stopPropagation();
    const newValue = event.target.checked;
    setIsDropshipEnabled(newValue);
    onPreferencesUpdate(row.id, { is_dropshipping_enabled: newValue });
  };*/

  // Debounced update function
  const debouncedUpdate = useDebounce((value) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      onPreferencesUpdate(row.id, { free_shipping_minimum: numValue });
    }
  }, 300);

  const handleFreeShipMinChange = (event) => {
    event.stopPropagation();
    const value = event.target.value;
    
    // Update local state immediately for responsive UI
    setFreeShipMin(value);
    
    // Debounce the actual update to preferences
    debouncedUpdate(value);
  };

  const handleFreeShipMinBlur = () => {
    // On blur, ensure we have a valid number
    const numValue = parseInt(freeShipMin, 10);
    if (isNaN(numValue) || numValue < 0) {
      // Reset to previous valid value
      const defaultValue =
        preferences[row.id]?.free_shipping_minimum ?? row.freeShippingMinimum;
      setFreeShipMin(defaultValue.toString());
      onPreferencesUpdate(row.id, { free_shipping_minimum: defaultValue });
    }
  };

  const handleWarehousesReorder = (reorderedWarehouses) => {
    // Create a new array reference to ensure React detects the change
    const newWarehouses = [...reorderedWarehouses];
    onPreferencesUpdate(row.id, { warehouses: newWarehouses });
  };

  return (
    <Accordion
      onChange={(_, expanded) => setIsExpanded(expanded)}
      className={isExpanded ? "expanded-row" : ""}
      sx={{
        mt: 0,
        borderRadius: 0,
        boxShadow: "none",
        borderTop: "1px solid rgba(0, 0, 0, 0.12)",
        "&::before": {
          display: "none",
        },
        "& .MuiPaper-root": {
          borderRadius: 0,
          boxShadow: "none",
        },
        "&:last-of-type": {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
        "&:first-of-type": {
          borderTop: "none",
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        },
        "&.Mui-expanded": {
          margin: "0",
        },
        "& .MuiAccordionSummary-root": {
          borderRadius: 0,
          transition: "none",
          "&:hover": {
            backgroundColor: "#F5F5F5",
          },
          "&.Mui-focused": {
            backgroundColor: "transparent",
          },
          "&:focus-within": {
            backgroundColor: "transparent",
          },
          ".MuiDataGrid-row:hover &": {
            backgroundColor: "#F5F5F5",
          },
          ".expanded-row &": {
            backgroundColor: "transparent !important",
          },
          "&.Mui-expanded": {
            backgroundColor: "#F5F5F5",
          },
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          padding: "0 16px",
          minHeight: "48px !important",
          "& .MuiAccordionSummary-content": {
            margin: "0",
            flex: 1,
            display: "flex",
            alignItems: "center",
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            marginRight: "8px",
          },
          height: "48px",
          "&.Mui-expanded": {
            minHeight: "48px",
            height: "48px",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box
            sx={{
              width: "180px",
              mr: 2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src={row.logoUrl}
              alt={row.vendor}
              style={{
                height: "24px",
                maxWidth: "170px",
                width: "auto",
                objectFit: "contain",
                objectPosition: "left",
              }}
            />
          </Box>
          <Box sx={{ width: "100px", display: "flex", alignItems: "center" }}>
            <Switch
              checked={!isDisabled}
              onChange={handleSwitchChange}
              onClick={(e) => e.stopPropagation()}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase': {
                  padding: '6px',
                  '&.Mui-checked': {
                    color: '#fff',  // White thumb when checked
                    transform: 'translateX(16px)',
                    '& + .MuiSwitch-track': {
                      backgroundColor: 'rgb(2, 129, 192)',  // Blue track when enabled
                      opacity: 1,
                    },
                  },
                },
                '& .MuiSwitch-thumb': {
                  width: 11,
                  height: 11,
                  backgroundColor: '#fff',  // White thumb color
                  border: '1px solid rgba(0, 0, 0, 0.2)',  // Light gray border
                  boxSizing: 'border-box',  // Ensure border is included in width/height
                  borderWidth: 'thin',
                },
                '& .MuiSwitch-track': {
                  borderRadius: 26 / 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  opacity: 1,
                },
              }}
            />
            <Typography variant="body2">
              {!isDisabled ? "Enabled" : "Disabled"}
            </Typography>
          </Box>
          <Box sx={{ width: "80px" }} />
          <Box sx={{ display: "flex", alignItems: "center", width: "260px" }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              Free Shipping Min:
            </Typography>
            <Typography variant="body2" sx={{ mr: 0.2 }}>$</Typography>
            <TextField
              value={freeShipMin}
              onChange={handleFreeShipMinChange}
              onBlur={handleFreeShipMinBlur}
              onClick={(e) => e.stopPropagation()}
              size="small"
              type="number"
              inputProps={{ min: 0 }}
              sx={{
                width: "50px",
                marginBottom: "3px",
                "& .MuiOutlinedInput-root": {
                  height: "32px",
                  "& input": {
                    padding: "8px 8px",
                    position: "relative",
                    top: "2px",
                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                      "-webkit-appearance": "none",
                      margin: 0,
                    },
                    "&[type=number]": {
                      "-moz-appearance": "textfield",
                    },
                  },
                },
              }}
            />
          </Box>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            position: "absolute",
            right: "48px",
            marginRight: "4px",
            "&:hover": {
              textDecoration: "underline",
              cursor: "pointer",
            },
          }}
        >
          Warehouses
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          boxShadow: "none",
          padding: 0,
          borderTop: "1px dashed rgba(0, 0, 0, 0.12)",
          borderRadius: 0,
          "& .MuiPaper-root": {
            borderRadius: 0,
          },
        }}
      >
        <WarehouseRankings 
          vendorId={row.id} 
          warehouses={row.warehouses} 
          onWarehousesReorder={handleWarehousesReorder}
        />
      </AccordionDetails>
    </Accordion>
  );
}, arePropsEqual);

export default VendorDropdown;